import { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "@/lib/api";
import { Card, fmtDate } from "@/components/ui-bits";
import { MessageSquare, Send, Loader2, FileText, Paperclip, Maximize2, X, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import QuickReplies from "@/components/QuickReplies";

export const TeamInbox = ({ department, title }) => {
  const [threads, setThreads] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [composing, setComposing] = useState(false);
  const [landlords, setLandlords] = useState([]);
  const [newLandlord, setNewLandlord] = useState("");
  const [newText, setNewText] = useState("");
  const [newFiles, setNewFiles] = useState([]);
  const [newUploading, setNewUploading] = useState(false);
  const [startSending, setStartSending] = useState(false);
  const fileRef = useRef(null);
  const composeFileRef = useRef(null);

  const load = () => api.get("/messages/threads").then(({ data }) => {
    const filtered = (data.threads || []).filter((t) => t.department === department);
    setThreads(filtered);
    setActiveId((prev) => prev || (filtered[0] && filtered[0].landlord_user_id));
  }).catch(() => setThreads([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [department]);

  const active = threads?.find((t) => t.landlord_user_id === activeId);

  const loadLandlords = () => {
    api.get("/messages/landlords").then(({ data }) => setLandlords(data.landlords || [])).catch(() => {});
  };
  const openCompose = () => { setComposing(true); if (!landlords.length) loadLandlords(); };
  const closeModal = () => { setExpanded(false); setComposing(false); setNewLandlord(""); setNewText(""); setNewFiles([]); };

  const onComposeUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setNewUploading(true);
    try {
      const recs = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "message");
        fd.append("entity_id", department);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        const { data: rec } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        recs.push({ file_id: rec.file_id, file_name: rec.original_filename, file_category: rec.category });
      }
      setNewFiles((prev) => [...prev, ...recs]);
    } catch { toast.error("Upload failed"); }
    finally { setNewUploading(false); if (composeFileRef.current) composeFileRef.current.value = ""; }
  };

  const startNew = async (e) => {
    if (e) e.preventDefault();
    if (!newLandlord || (!newText.trim() && !newFiles.length)) return;
    setStartSending(true);
    try {
      if (newFiles.length) {
        for (let i = 0; i < newFiles.length; i++) {
          const f = newFiles[i];
          await api.post("/messages", { department, landlord_user_id: newLandlord, body: i === 0 ? newText.trim() : "", file_id: f.file_id, file_name: f.file_name, file_category: f.file_category });
        }
      } else {
        await api.post("/messages", { department, landlord_user_id: newLandlord, body: newText.trim() });
      }
      const lid = newLandlord;
      setNewText(""); setNewLandlord(""); setNewFiles([]); setComposing(false);
      await load();
      setActiveId(lid);
    } catch { toast.error("Could not send message"); }
    finally { setStartSending(false); }
  };

  const reply = async (e) => {
    e.preventDefault();
    if (!text.trim() || !active) return;
    setSending(true);
    try {
      await api.post("/messages", { department, landlord_user_id: active.landlord_user_id, body: text.trim() });
      setText("");
      await load();
    } catch { toast.error("Could not send reply"); }
    finally { setSending(false); }
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !active) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("entity_type", "message");
        fd.append("entity_id", department);
        fd.append("bucket", "attachments");
        fd.append("file", f);
        const { data: rec } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        await api.post("/messages", { department, landlord_user_id: active.landlord_user_id, body: "", file_id: rec.file_id, file_name: rec.original_filename, file_category: rec.category });
      }
      await load();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <>
      <button onClick={() => setExpanded(true)} data-testid={`team-inbox-${department}`}
        className="w-full mb-6 flex items-center gap-3 px-4 py-3 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition-all text-left">
        <MessageSquare size={16} className="text-emerald-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900">{title || `${department} — Landlord Messages`}</p>
          <p className="text-xs text-slate-400">{threads === null ? "Loading…" : threads.length === 0 ? "No landlord messages yet" : `${threads.length} conversation${threads.length === 1 ? "" : "s"} · tap to open`}</p>
        </div>
        {threads && threads.length > 0 && <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{threads.length}</span>}
        <Maximize2 size={15} className="text-slate-400 shrink-0" />
      </button>

      {expanded && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6 bg-slate-900/50 backdrop-blur-sm" onClick={closeModal}>
          <div data-testid={`team-inbox-modal-${department}`} className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
              <div>
                <p className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><MessageSquare size={16} className="text-emerald-600" /> {title || `${department} — Landlord Messages`}</p>
                <p className="text-xs text-slate-400">Messages to and from landlords for the {department} team.</p>
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`inbox-new-${department}`} onClick={openCompose}
                  className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"><Plus size={14} /> New message</button>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
              </div>
            </div>
            {composing ? (
              <form onSubmit={startNew} className="flex-1 min-h-0 flex flex-col p-6 gap-4 overflow-y-auto" data-testid={`inbox-compose-${department}`}>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-1.5"><UserPlus size={14} className="text-emerald-600" /> Send to landlord</label>
                  <select data-testid={`inbox-new-landlord-${department}`} value={newLandlord} onChange={(e) => setNewLandlord(e.target.value)}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600">
                    <option value="">{landlords.length ? "Choose a landlord…" : "Loading landlords…"}</option>
                    {landlords.map((l) => <option key={l.user_id} value={l.user_id}>{l.name}{l.email ? ` · ${l.email}` : ""}</option>)}
                  </select>
                  {landlords.length === 0 && <p className="text-xs text-slate-400 mt-1">Only landlords with a login account appear here.</p>}
                </div>
                <div className="flex-1 min-h-0 flex flex-col">
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-slate-700">Message</label>
                    <QuickReplies align="right"
                      landlordName={(landlords.find((l) => l.user_id === newLandlord) || {}).name || ""}
                      onPick={(txt) => setNewText((prev) => (prev.trim() ? prev + "\n" + txt : txt))} />
                  </div>
                  <textarea data-testid={`inbox-new-text-${department}`} value={newText} onChange={(e) => setNewText(e.target.value)}
                    placeholder="Write your message to the landlord…"
                    className="flex-1 min-h-[140px] resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                </div>
                <input ref={composeFileRef} type="file" multiple className="hidden" data-testid={`inbox-new-file-input-${department}`}
                  accept="image/*,video/*,application/pdf,.xls,.xlsx,.csv,.doc,.docx" onChange={onComposeUpload} />
                {newFiles.length > 0 && (
                  <div className="space-y-1.5" data-testid={`inbox-new-attachment-${department}`}>
                    {newFiles.map((f, i) => (
                      <div key={`${f.file_id}-${i}`} className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2" data-testid={`inbox-new-attachment-${department}-${i}`}>
                        <FileText size={15} className="text-emerald-600 shrink-0" />
                        <span className="text-sm text-slate-700 truncate flex-1">{f.file_name}</span>
                        <button type="button" data-testid={`inbox-new-attachment-remove-${department}-${i}`} onClick={() => setNewFiles((prev) => prev.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-500"><X size={15} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" data-testid={`inbox-new-attach-${department}`} onClick={() => composeFileRef.current?.click()} disabled={newUploading}
                  className="self-start flex items-center gap-2 text-sm font-medium text-slate-600 border border-slate-300 rounded-lg px-3 py-2 hover:bg-slate-50 disabled:opacity-60" title="Attach PDF, photo, video or Excel">
                  {newUploading ? <Loader2 size={15} className="animate-spin" /> : <Paperclip size={15} />} {newFiles.length ? "Add another file" : "Attach a file"}
                </button>
                <div className="flex items-center justify-end gap-2">
                  <button type="button" onClick={() => { setComposing(false); setNewFiles([]); }} className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                  <button type="submit" data-testid={`inbox-new-send-${department}`} disabled={!newLandlord || (!newText.trim() && !newFiles.length) || startSending || newUploading}
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                    {startSending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send message{newFiles.length > 1 ? ` (${newFiles.length})` : ""}
                  </button>
                </div>
              </form>
            ) : threads === null ? (
              <p className="text-sm text-slate-400 py-8 text-center">Loading…</p>
            ) : threads.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-6" data-testid={`team-inbox-empty-${department}`}>
                <MessageSquare size={26} className="text-slate-300" />
                <p className="text-sm text-slate-400">No landlord messages for the {department} team yet.</p>
                <button onClick={openCompose} className="text-sm font-semibold px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-1.5"><Plus size={15} /> Start a new message</button>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 flex-1 min-h-0">
                <div className="border-r border-slate-100 overflow-y-auto">
                  {threads.map((t) => {
                    const last = t.messages[t.messages.length - 1];
                    return (
                      <button key={t.landlord_user_id} data-testid={`inbox-thread-${t.landlord_user_id}`} onClick={() => setActiveId(t.landlord_user_id)}
                        className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${activeId === t.landlord_user_id ? "bg-emerald-50/60" : ""}`}>
                        <p className="text-sm font-medium text-slate-800 truncate">{t.landlord_name}</p>
                        <p className="text-xs text-slate-400 truncate">{last?.body || (last?.file_name ? `📎 ${last.file_name}` : "")}</p>
                      </button>
                    );
                  })}
                </div>
                <div className="md:col-span-2 flex flex-col min-h-0">
                  <div className="p-4 space-y-2 overflow-y-auto flex-1" data-testid={`inbox-conversation-${department}`}>
                    {active?.messages.map((m) => {
                      const staff = m.from_role === "admin" || m.from_role === "staff";
                      return (
                        <div key={m.message_id} className={`flex ${staff ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${staff ? "bg-emerald-600 text-white rounded-br-sm" : "bg-slate-100 text-slate-800 rounded-bl-sm"}`}>
                            {!staff && <p className="text-[11px] font-semibold text-slate-500 mb-0.5">{m.from_name}</p>}
                            {m.file_id && (
                              m.file_category === "photo" ? <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer"><img src={fileUrl(m.file_id)} alt="" className="rounded-lg max-h-40 mb-1" /></a>
                              : m.file_category === "video" ? <video src={fileUrl(m.file_id)} controls className="rounded-lg max-h-40 mb-1" />
                              : <a href={fileUrl(m.file_id)} target="_blank" rel="noreferrer" className={`flex items-center gap-2 mb-1 underline ${staff ? "text-white" : "text-emerald-700"}`}><FileText size={14} /> {m.file_name || "Attachment"}</a>
                            )}
                            {m.body && <p className="whitespace-pre-wrap">{m.body}</p>}
                            <p className={`text-[10px] mt-1 ${staff ? "text-emerald-100" : "text-slate-400"}`}>{fmtDate(m.date)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <form onSubmit={reply} className="flex items-center gap-2 p-3 border-t border-slate-100 shrink-0">
                    <input ref={fileRef} type="file" multiple className="hidden" data-testid={`inbox-file-${department}`}
                      accept="image/*,video/*,application/pdf,.xls,.xlsx,.csv,.doc,.docx" onChange={onUpload} />
                    <button type="button" data-testid={`inbox-attach-${department}`} onClick={() => fileRef.current?.click()} disabled={uploading || !active}
                      className="w-10 h-10 shrink-0 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center disabled:opacity-60" title="Attach PDF, photo, video or Excel">
                      {uploading ? <Loader2 size={16} className="animate-spin" /> : <Paperclip size={16} />}
                    </button>
                    <QuickReplies compact align="left"
                      landlordName={active?.landlord_name || ""}
                      onPick={(txt) => setText((prev) => (prev.trim() ? prev + "\n" + txt : txt))} />
                    <textarea data-testid={`inbox-reply-input-${department}`} value={text} onChange={(e) => setText(e.target.value)} rows={1}
                      placeholder={active ? `Reply to ${active.landlord_name}…` : "Select a conversation"}
                      className="flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                    <button type="submit" data-testid={`inbox-reply-send-${department}`} disabled={sending || !text.trim()}
                      className="w-10 h-10 shrink-0 rounded-full bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center disabled:opacity-50">
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    </button>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default TeamInbox;
